import { SOCIETIES } from "@/constants/legal/societies/societies";

export type LegalOnboardingDocumentTipo = "Obligatorio" | "Opcional";

export interface LegalOnboardingDocument {
  id: number;
  idDocumento?: number;
  nombre: string;
  urlDescarga?: string;
  obligatorio?: boolean;
  idTipoArchivo?: number;
  tipo?: LegalOnboardingDocumentTipo;
  estado: string | null;
  requiereReenvio: boolean;
  [key: string]: unknown;
}

type DocCatalogItem = {
  idTipoArchivo: number;
  nombre: string;
  tipo: LegalOnboardingDocumentTipo;
};

const docsByTipoSociedad: Record<string, DocCatalogItem[]> = Object.fromEntries(
  SOCIETIES.map((s) => [
    s.name,
    s.documents.map((d) => ({
      idTipoArchivo: d.id,
      nombre: d.name,
      tipo: d.required ? "Obligatorio" : "Opcional",
    })),
  ]),
);

const normalizeSocietyKey = (value: string) =>
  value
    .normalize("NFD")
    .replaceAll(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replaceAll(/\bsociedad\b/g, "soc")
    .replaceAll(/\bresponsabilidad\b/g, "resp")
    .replaceAll(/\blimitada\b/g, "ltda")
    .replaceAll(/\bacciones\b/g, "acc")
    .replaceAll(/\bsimplificadas?\b/g, "sas")
    .replaceAll(/\banonima\b/g, "sa")
    .replaceAll(/\bde\b/g, "")
    .replaceAll(/[^a-z0-9]+/g, "")
    .trim();

const docsByTipoSociedadNormalized: Record<string, DocCatalogItem[]> =
  Object.fromEntries(
    Object.entries(docsByTipoSociedad).map(([k, v]) => [
      normalizeSocietyKey(k),
      v,
    ]),
  );

const normalizeDocName = normalizeSocietyKey;

const getDocTipo = (
  d: LegalOnboardingDocument,
): LegalOnboardingDocumentTipo | undefined => {
  if (typeof d?.obligatorio === "boolean") {
    return d.obligatorio ? "Obligatorio" : "Opcional";
  }
  return d?.tipo;
};

const getFiniteNumber = (value: unknown): number | null => {
  const num = typeof value === "number" ? value : Number(value);
  return Number.isFinite(num) ? num : null;
};

const mapDocsWithoutCatalog = (
  documentos?: LegalOnboardingDocument[],
): LegalOnboardingDocument[] =>
  (documentos ?? []).map((d) => {
    const tipo = getDocTipo(d);
    const idTipoArchivo = getFiniteNumber(d?.idTipoArchivo);
    const hasTipoId = idTipoArchivo !== null;

    return {
      ...d,
      idDocumento: d?.id,
      id: hasTipoId ? (idTipoArchivo as number) : d?.id,
      idTipoArchivo: hasTipoId ? (idTipoArchivo as number) : d?.idTipoArchivo,
      tipo,
    };
  });

const buildLoadedIndexes = (documentos?: LegalOnboardingDocument[]) => {
  const loaded = (documentos ?? []).filter(Boolean);
  const loadedMap = new Map<number, LegalOnboardingDocument>();
  const loadedByName = new Map<string, LegalOnboardingDocument>();

  for (const d of loaded) {
    const idTipoArchivo = getFiniteNumber(d?.idTipoArchivo ?? d?.id);
    if (idTipoArchivo === null) continue;
    if (!loadedMap.has(idTipoArchivo)) loadedMap.set(idTipoArchivo, d);

    const docName = typeof d?.nombre === "string" ? d.nombre : "";
    if (!docName.trim().length) continue;
    const normalized = normalizeDocName(docName);
    if (normalized.length && !loadedByName.has(normalized)) {
      loadedByName.set(normalized, d);
    }
  }

  return { loadedMap, loadedByName };
};

const buildMissingCatalogDoc = (e: DocCatalogItem): LegalOnboardingDocument => ({
  id: e.idTipoArchivo,
  idTipoArchivo: e.idTipoArchivo,
  nombre: e.nombre,
  tipo: e.tipo,
  obligatorio: e.tipo === "Obligatorio",
  estado: null,
  requiereReenvio: false,
  urlDescarga: undefined,
});

const mergeCatalogDoc = (
  e: DocCatalogItem,
  fromBe: LegalOnboardingDocument,
): LegalOnboardingDocument => ({
  ...fromBe,
  idDocumento: fromBe?.id,
  id: e.idTipoArchivo,
  idTipoArchivo: e.idTipoArchivo,
  nombre: e.nombre,
  tipo: e.tipo,
  obligatorio:
    typeof fromBe?.obligatorio === "boolean"
      ? fromBe.obligatorio
      : e.tipo === "Obligatorio",
  estado: fromBe?.estado ?? null,
  requiereReenvio: Boolean(fromBe?.requiereReenvio),
});

/**
 * Combina los documentos que llegaron del backend con el catálogo de
 * documentos esperados para el tipo de sociedad, completando los que
 * falten y homologando id/tipo contra el catálogo.
 */
export const mergeDocsForSociety = (
  tipoSociedad?: string,
  documentos?: LegalOnboardingDocument[],
): LegalOnboardingDocument[] => {
  const expected = tipoSociedad
    ? docsByTipoSociedadNormalized[normalizeSocietyKey(tipoSociedad)]
    : undefined;
  if (!expected?.length) {
    return mapDocsWithoutCatalog(documentos);
  }

  const { loadedMap, loadedByName } = buildLoadedIndexes(documentos);

  return expected.map((e) => {
    const fromBe =
      loadedMap.get(e.idTipoArchivo) ??
      loadedByName.get(normalizeDocName(e.nombre));
    if (!fromBe) {
      return buildMissingCatalogDoc(e);
    }

    return mergeCatalogDoc(e, fromBe);
  });
};
